/**
 * The aanvraag form's decision logic, extracted so it is unit-testable with
 * boundary values (user call, 2026-08-21: "any school book gives examples of
 * providing stupid values like this as input" — and the dead submit button
 * proved the point; inline component logic never met a stupid value in CI).
 * The component renders; this file decides.
 */

import { plainDate } from "@bivak/domain";

export const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;

export type Invulling = {
  van: string;
  tot: string;
  personen: string;
  groepsnaam: string;
  contactNaam: string;
  email: string;
  telefoon: string;
};

export type Veldnaam = "datums" | "personen" | "groepsnaam" | "contactNaam" | "email" | "telefoon";

/**
 * Everything still standing between the renter and the submit, by name — a
 * disabled button without a reason is a dead end (persona + user feedback).
 * Mirrors the SERVER's zod bounds: anything this passes, the API accepts.
 *
 * The headcount estimate is only asked — and only required — for overnight
 * stays: a dag prices flat, and the under-31 split is settled from actuals at
 * the afrekening, never claimed at booking (user, 2026-08-22).
 */
export function ontbrekendeVelden(invulling: Invulling, overnacht: boolean): Veldnaam[] {
  const aantal = Number.parseInt(invulling.personen, 10);
  const ontbreekt: Veldnaam[] = [];

  if (!datumsGeldig(invulling.van, invulling.tot)) ontbreekt.push("datums");
  if (overnacht && (!Number.isInteger(aantal) || aantal < 1 || aantal > 80)) {
    ontbreekt.push("personen");
  }
  if (invulling.groepsnaam.trim().length < 2) ontbreekt.push("groepsnaam");
  if (invulling.contactNaam.trim().length < 2) ontbreekt.push("contactNaam");
  if (!invulling.email.includes("@")) ontbreekt.push("email");
  if (invulling.telefoon.trim().length < 6) ontbreekt.push("telefoon");
  return ontbreekt;
}

export function datumsGeldig(van: string, tot: string): boolean {
  return ISO_DATUM.test(van) && ISO_DATUM.test(tot) && van <= tot;
}

/**
 * Does [van, tot] touch an unavailable period? Periods arrive half-open from
 * the API ([from, to)); the stay's departure day may equal a period's `from`
 * (the wisseldag) without conflict — but a departure INSIDE one conflicts.
 */
export function periodeBezet(
  van: string,
  tot: string,
  unavailable: readonly { from: string; to: string }[],
): boolean {
  if (!datumsGeldig(van, tot)) return false;
  return unavailable.some((periode) => van < periode.to && periode.from <= tot);
}

/**
 * Which month the embedded grid shows. Anchored on TODAY, never on the
 * arrival: anchoring on the arrival made the back arrow dead (the offset
 * clamps at its anchor) and every fresh first click re-anchored the grid and
 * shifted the view a month (user feedback: "click november, jump december").
 * Manual navigation wins; a TYPED arrival deliberately jumps the view.
 */
export function gridMaandOffset(
  handmatig: number | null,
  anchorIso: string,
  van: string,
  maanden: number,
): number {
  if (handmatig !== null) return klemOffset(handmatig, maanden);
  if (!ISO_DATUM.test(van)) return 0;
  const anker = plainDate(anchorIso);
  const doel = plainDate(van);
  return klemOffset((doel.year - anker.year) * 12 + (doel.month - anker.month), maanden);
}

function klemOffset(offset: number, maanden: number): number {
  return Math.max(0, Math.min(offset, maanden - 1));
}
